import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getByIdPA, addPA } from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import { addQuestionToSession, createQuestion } from '../../context/questions/QuestionActions'
import { 
  FaCalendarAlt, 
  FaPlus, 
  FaCheck,
  FaTimes,
  FaArrowLeft,
  FaArrowRight,
  FaQuestionCircle,
  FaImages
} from 'react-icons/fa'
import { Card, Row, Col, Button, Badge } from 'react-bootstrap'
import AddQuestionToCanvas from '../canvas/AddQuestionToCanvas'
import AddPhotoToCanvas from '../canvas/AddPhototoCanvas'
import './SessionSetup.css'

export default function SessionSetup() {
  const navigate = useNavigate()
  const params = useParams()
  const { authenticatedUser, setAlert } = useUserContext()

  const [session, setSession] = useState({})
  const [questions, setQuestions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(1) // 1: Questions, 2: Photos per question

  // Estados para modales (copiados de Canvas.js)
  const [show, setShow] = useState(false)
  const [showPhoto, setShowPhoto] = useState(false)
  const [newQuestion, setNewQuestion] = useState(false)
  const [question, setQuestion] = useState('')
  const [selectQuestion, setSelectQuestion] = useState(0)
  const [photoObjs, setPhotosObjs] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)

  const fetchSessionData = async () => {
    setIsLoading(true)
    try {
      const sessionData = await getByIdPA({ 
        path: 'sessions', 
        id: params.id, 
        token: authenticatedUser.value 
      })
      
      if (sessionData && sessionData.sessionId) {
        setSession(sessionData)
        // Obtener preguntas existentes de la sesión
        const canvasData = await getByIdPA({ 
          path: 'canvas', 
          id: sessionData.sessionCode, 
          token: authenticatedUser.value 
        })
        if (canvasData && canvasData.questions) {
          setQuestions(canvasData.questions)
        }
      } else {
        setAlert({
          message: 'No se pudo cargar la información de la sesión',
          msgType: 'warning'
        })
        navigate('/sessions')
      }
    } catch (error) {
      setAlert({
        message: 'Error al cargar la sesión. Por favor, inténtalo de nuevo.',
        msgType: 'error'
      })
      navigate('/sessions')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSessionData()
  }, [params.id])

  const handleAddQuestion = () => {
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
    setShowPhoto(false)
  }

  const onClickSelectPics = async () => {
    setShow(false)
    setShowPhoto(true)

    if (question.length > 3 && newQuestion) {
      let response = await createQuestion({
        path: 'questions',
        data: {
          question: question,
          "type": "NEW"
        },
        token: authenticatedUser.value
      })

      if (response.status === 200 && response.data) {
        setSelectQuestion(response.data.questionId)
      }
      if (response.status !== 200) {
        setAlert({
          message: response.message,
          msgType: 'warning'
        })
      }
    }
  }

  const handleOnSelectQuestion = (e) => {
    const value = e.target.value
    setSelectQuestion(value)
  }

  const handleChangeQuestion = async (e) => {
    const value = e.target.value;
    if (value.length > 3 && show && newQuestion) {
      setQuestion(value)
    }
  }

  const handleAddNewQuestionToggle = () => {
    setNewQuestion(!newQuestion)
    setQuestion('');
  }

  const saveQuestionToSession = async () => {
    if (photoObjs.length > 0 && selectQuestion !== 0) {
      const data = {
        "questionId": Number(selectQuestion),
        "responses": photoObjs
      }
      
      const record = await addQuestionToSession({ 
        path: 'sessionQuestion', 
        data, 
        token: authenticatedUser.value, 
        sessionId: session.sessionId 
      });

      if (Object.prototype.hasOwnProperty.call(record, 'questionId')) {
        // Actualizar lista de preguntas
        setQuestions([...questions, record])
        setAlert({
          message: 'Pregunta agregada exitosamente',
          msgType: 'success'
        })
        setShowPhoto(false)
        setPhotosObjs([])
        setQuestion('')
        setSelectQuestion(0)
      } else {
        setAlert({
          message: record.message,
          msgType: 'warning'
        })
      }
    }
  }

  const handleRemoveQuestion = async (questionToRemove) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta pregunta?')) {
      // Aquí iría la lógica para eliminar la pregunta
      // Por ahora solo la removemos del estado local
      setQuestions(questions.filter(q => q.sessionQstId !== questionToRemove.sessionQstId))
      setAlert({
        message: 'Pregunta eliminada exitosamente',
        msgType: 'success'
      })
    }
  }

  const handleFinishSetup = () => {
    if (questions.length === 0) {
      setAlert({
        message: 'Debes agregar al menos una pregunta para continuar',
        msgType: 'warning'
      })
      return
    }

    setAlert({
      message: 'Configuración completada exitosamente. Tu sesión está lista para usar.',
      msgType: 'success'
    })
    navigate('/sessions')
  }

  const handleBackToSessions = () => {
    navigate('/sessions')
  }

  if (isLoading) {
    return (
      <div className="session-setup-container">
        <div className="container">
          <div className="loading-state">
            <FaCalendarAlt className="loading-icon" />
            <p className="mt-3">Cargando configuración de sesión...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="session-setup-container">
      <div className="container">
        {/* Header */}
        <div className="setup-header">
          <div className="header-content">
            <div className="header-icon">
              <div className="icon-circle">
                <FaCalendarAlt className="icon" />
              </div>
            </div>
            <div className="header-text">
              <h1 className="header-title">Configurar Sesión</h1>
              <h2 className="session-name">{session.sessionName}</h2>
              <p className="header-subtitle">
                Agrega preguntas y selecciona las fotos para cada una
              </p>
            </div>
          </div>
          <Button
            variant="outline-secondary"
            onClick={handleBackToSessions}
            className="back-button"
          >
            <FaArrowLeft className="me-2" />
            Volver al Listado
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="setup-steps">
          <div className="step-item active">
            <div className="step-number">1</div>
            <div className="step-text">Configurar Preguntas</div>
          </div>
          <div className="step-divider"></div>
          <div className="step-item">
            <div className="step-number">2</div>
            <div className="step-text">Sesión Lista</div>
          </div>
        </div>

        {/* Questions Section */}
        <Card className="setup-card">
          <Card.Header className="setup-card-header">
            <div className="card-header-content">
              <div className="header-info">
                <FaQuestionCircle className="header-icon" />
                <div>
                  <h3>Preguntas de la Sesión</h3>
                  <p>Agrega las preguntas que los invitados deberán responder</p>
                </div>
              </div>
              <Button
                variant="primary"
                onClick={handleAddQuestion}
                className="add-question-btn"
              >
                <FaPlus className="me-2" />
                Agregar Pregunta
              </Button>
            </div>
          </Card.Header>

          <Card.Body>
            {questions.length === 0 ? (
              <div className="empty-state">
                <FaQuestionCircle className="empty-icon" />
                <h4>No hay preguntas configuradas</h4>
                <p>Agrega al menos una pregunta para que los invitados puedan participar</p>
              </div>
            ) : (
              <div className="questions-list">
                {questions.map((question, index) => (
                  <div key={question.sessionQstId} className="question-item">
                    <div className="question-content">
                      <div className="question-info">
                        <div className="question-number">#{index + 1}</div>
                        <div className="question-text">{question.question}</div>
                        <Badge bg="success" className="photos-count">
                          <FaImages className="me-1" />
                          {question.responses?.length || 0} fotos
                        </Badge>
                      </div>
                      <div className="question-actions">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveQuestion(question)}
                          className="remove-btn"
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Action Buttons */}
        <div className="setup-actions">
          <Button
            variant="outline-secondary"
            onClick={handleBackToSessions}
            className="cancel-button"
          >
            <FaTimes className="me-2" />
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleFinishSetup}
            className="finish-button"
            disabled={questions.length === 0}
          >
            <FaCheck className="me-2" />
            Finalizar Configuración
          </Button>
        </div>

        {/* Modales */}
        <AddQuestionToCanvas
          showModalQuestion={show}
          handleClose={handleClose}
          onClickNext={onClickSelectPics}
          onblurCreateQuestion={handleChangeQuestion}
          newQuestion={newQuestion}
          setNewQuestion={handleAddNewQuestionToggle}
          handleOnSelectQuestion={handleOnSelectQuestion}
        />

        <AddPhotoToCanvas
          showModalPic={showPhoto}
          handleClose={() => setShowPhoto(false)}
          setPhotosObjs={setPhotosObjs}
          saveQuestionToSession={saveQuestionToSession}
        />
      </div>
    </div>
  )
}
